<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mr-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon> </v-btn
          >Edit Coupon
          <v-chip class="ml-4" small color="primary" text-color="white">
            {{ form.code }}
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn
            small
            rounded
            color="info"
            :to="`/coupons/${$route.params.couponId}/usages`"
          >
            <v-icon left small>mdi-account-multiple</v-icon> Usage
          </v-btn>
        </v-card-title>
        <page-loader :loading="isFetching" />
        <v-card-text>
          <v-alert dense text type="info" class="mb-4">
            Used <strong>{{ form.used_count || 0 }}</strong> time(s)
            <span v-if="form.usage_limit">
              out of <strong>{{ form.usage_limit }}</strong></span
            >
            <span v-else> (unlimited)</span>
          </v-alert>

          <h4 class="mb-3 grey--text text--darken-2">Coupon Code</h4>
          <v-row>
            <v-col sm="3">
              <v-text-field
                v-model="form.code"
                outlined
                label="Coupon Code *"
                :error-messages="errors.code"
                @input="form.code = (form.code || '').toUpperCase()"
              ></v-text-field>
            </v-col>
            <v-col sm="5">
              <v-text-field
                v-model="form.title"
                outlined
                label="Title"
                :error-messages="errors.title"
              ></v-text-field>
            </v-col>
            <v-col sm="3">
              <v-switch
                v-model="form.is_active"
                label="Active"
                :true-value="true"
                :false-value="false"
              ></v-switch>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>
          <h4 class="mb-3 grey--text text--darken-2">Discount</h4>
          <v-row>
            <v-col sm="3">
              <v-autocomplete
                outlined
                v-model="form.discount_type"
                :items="discountTypes"
                label="Discount Type *"
                :error-messages="errors.discount_type"
              ></v-autocomplete>
            </v-col>
            <v-col sm="3">
              <v-text-field
                v-model="form.discount_value"
                outlined
                type="number"
                :label="
                  form.discount_type == 'percent'
                    ? 'Discount (%) *'
                    : 'Discount (₹) *'
                "
                :error-messages="errors.discount_value"
              ></v-text-field>
            </v-col>
            <v-col sm="3" v-if="form.discount_type == 'percent'">
              <v-text-field
                v-model="form.max_discount_amount"
                outlined
                type="number"
                label="Max Discount (&#8377;)"
                :error-messages="errors.max_discount_amount"
              ></v-text-field>
            </v-col>
            <v-col sm="3">
              <v-text-field
                v-model="form.min_order_amount"
                outlined
                type="number"
                label="Min Order Amount (&#8377;)"
                :error-messages="errors.min_order_amount"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="3">
              <v-autocomplete
                outlined
                v-model="form.applicable_on"
                :items="applicableOnItems"
                label="Applicable On *"
                :error-messages="errors.applicable_on"
              ></v-autocomplete>
            </v-col>
            <v-col sm="9">
              <v-autocomplete
                outlined
                multiple
                chips
                deletable-chips
                v-model="form.plan_ids"
                :items="planItems"
                label="Limit to Plans"
                hint="Leave empty to allow all plans"
                persistent-hint
                clearable
                :error-messages="errors.plan_ids"
              >
                <template v-slot:prepend-item>
                  <v-list-item ripple @mousedown.prevent @click="toggleSelectAllPlans">
                    <v-list-item-action>
                      <v-icon :color="form.plan_ids.length > 0 ? 'primary' : ''">
                        {{ selectAllPlansIcon }}
                      </v-icon>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>Select All</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-2"></v-divider>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>
          <h4 class="mb-3 grey--text text--darken-2">Usage Limit</h4>
          <v-row>
            <v-col sm="3">
              <v-text-field
                v-model="form.usage_limit"
                outlined
                type="number"
                label="Total Uses"
                hint="Blank = unlimited"
                persistent-hint
                :error-messages="errors.usage_limit"
              ></v-text-field>
            </v-col>
            <v-col sm="3">
              <v-text-field
                v-model="form.usage_limit_per_user"
                outlined
                type="number"
                label="Uses Per User"
                :error-messages="errors.usage_limit_per_user"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>
          <h4 class="mb-3 grey--text text--darken-2">Validity</h4>
          <v-row>
            <v-col sm="3">
              <v-menu
                v-model="validFromMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.valid_from"
                    label="Valid From"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    hide-details="auto"
                    clearable
                    @click:clear="form.valid_from = null"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.valid_from"
                  @input="validFromMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col sm="3">
              <v-text-field
                v-model="form.validity_days"
                outlined
                type="number"
                label="Valid For (days)"
                hint="Changing this recalculates Valid Till"
                persistent-hint
                :error-messages="errors.validity_days"
              ></v-text-field>
            </v-col>
            <v-col sm="3">
              <v-menu
                v-model="validTillMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.valid_till"
                    label="Valid Till"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    hide-details="auto"
                    clearable
                    @click:clear="form.valid_till = null"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.valid_till"
                  @input="validTillMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>
          <h4 class="mb-3 grey--text text--darken-2">Influencer</h4>
          <v-row>
            <v-col sm="3">
              <v-switch
                v-model="form.is_influencer_coupon"
                label="Influencer Coupon"
              ></v-switch>
            </v-col>
            <template v-if="form.is_influencer_coupon">
              <v-col sm="3">
                <v-text-field
                  v-model="form.influencer_name"
                  outlined
                  label="Influencer Name"
                  :error-messages="errors.influencer_name"
                ></v-text-field>
              </v-col>
              <v-col sm="3">
                <v-text-field
                  v-model="form.influencer_phone"
                  outlined
                  label="Influencer Phone"
                  :error-messages="errors.influencer_phone"
                ></v-text-field>
              </v-col>
              <v-col sm="3">
                <v-autocomplete
                  outlined
                  v-model="form.influencer_platform"
                  :items="platformItems"
                  label="Platform"
                  :error-messages="errors.influencer_platform"
                ></v-autocomplete>
              </v-col>
            </template>
          </v-row>
          <v-row>
            <v-col sm="12">
              <v-textarea
                v-model="form.description"
                outlined
                rows="2"
                label="Description"
                :error-messages="errors.description"
              ></v-textarea>
            </v-col>
          </v-row>

          <v-divider class="my-5"> </v-divider>
          <v-btn v-can:edit="'coupons'"
            rounded
            color="primary"
            :loading="isLoading"
            :disabled="isLoading"
            @click="save"
          >
            Update Coupon
          </v-btn>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>
<script>
import axios from "axios";

export default {
  name: "CouponEditPage",
  data() {
    return {
      form: {
        is_active: true,
        plan_ids: [],
      },
      old_json: null,
      discountTypes: [
        { id: "percent", text: "PERCENT (%)", value: "percent" },
        { id: "fixed", text: "FLAT (₹)", value: "fixed" },
      ],
      applicableOnItems: [
        { id: "plan", text: "PLAN PURCHASE", value: "plan" },
        { id: "wallet", text: "WALLET TOP-UP", value: "wallet" },
        { id: "all", text: "BOTH", value: "all" },
      ],
      platformItems: [
        { id: "instagram", text: "INSTAGRAM", value: "instagram" },
        { id: "youtube", text: "YOUTUBE", value: "youtube" },
        { id: "facebook", text: "FACEBOOK", value: "facebook" },
        { id: "other", text: "OTHER", value: "other" },
      ],
      planItems: [],
      validFromMenu: false,
      validTillMenu: false,
      isLoading: false,
      isFetching: false,
      isComputingValidity: false,
    };
  },
  computed: {
    selectAllPlansIcon() {
      if (!this.form.plan_ids.length) return "mdi-checkbox-blank-outline";
      if (this.form.plan_ids.length === this.planItems.length)
        return "mdi-close-box";
      return "mdi-minus-box";
    },
  },
  watch: {
    "form.valid_from"() {
      if (this.isComputingValidity) return;
      if (this.form.validity_days) {
        this.recalcValidTillFromDays();
      } else if (this.form.valid_till) {
        this.recalcDaysFromValidTill();
      }
    },
    "form.validity_days"() {
      if (this.isComputingValidity) return;
      this.recalcValidTillFromDays();
    },
    "form.valid_till"() {
      if (this.isComputingValidity) return;
      this.recalcDaysFromValidTill();
    },
  },
  async mounted() {
    this.isFetching = true;
    try {
      await this.getPlans();
    } finally {
      this.isFetching = false;
    }
  },
  methods: {
    toggleSelectAllPlans() {
      this.$nextTick(() => {
        this.form.plan_ids =
          this.form.plan_ids.length === this.planItems.length
            ? []
            : this.planItems.map((plan) => plan.value);
      });
    },
    parseDateOnly(str) {
      const [y, m, d] = str.split("-").map(Number);
      return new Date(y, m - 1, d);
    },
    formatDateOnly(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    recalcValidTillFromDays() {
      if (!this.form.valid_from || !this.form.validity_days) return;
      const days = parseInt(this.form.validity_days, 10);
      if (isNaN(days)) return;
      const from = this.parseDateOnly(this.form.valid_from);
      from.setDate(from.getDate() + days);
      this.isComputingValidity = true;
      this.form.valid_till = this.formatDateOnly(from);
      this.$nextTick(() => {
        this.isComputingValidity = false;
      });
    },
    recalcDaysFromValidTill() {
      if (!this.form.valid_from || !this.form.valid_till) return;
      const from = this.parseDateOnly(this.form.valid_from);
      const till = this.parseDateOnly(this.form.valid_till);
      const diffDays = Math.round((till - from) / 86400000);
      this.isComputingValidity = true;
      this.form.validity_days = diffDays >= 0 ? diffDays : null;
      this.$nextTick(() => {
        this.isComputingValidity = false;
      });
    },
    async getPlans() {
      await axios
        .get(`plans`)
        .then((response) => {
          this.planItems = (response.data.data || []).map((plan) => ({
            id: plan.id,
            value: plan.id,
            text: `${plan.tier || "Plan"} - ${plan.no_of_dogs || 0} dog(s), ${
              plan.no_of_walks || 0
            } walks (₹${plan.final_amount || 0})`,
          }));
        })
        .catch((error) => {
          console.error(error);
        });
      this.getData();
    },
    async getData() {
      this.isLoading = true;
      let encryptedId = this.$route.params.couponId;
      await axios
        .get(`/coupons/${encryptedId}`)
        .then((response) => {
          const coupon = response.data.data;
          // The API sends full timestamps, the date picker wants YYYY-MM-DD
          coupon.valid_from = coupon.valid_from
            ? String(coupon.valid_from).substring(0, 10)
            : null;
          coupon.valid_till = coupon.valid_till
            ? String(coupon.valid_till).substring(0, 10)
            : null;
          coupon.plan_ids = coupon.plan_ids || [];
          this.isComputingValidity = true;
          this.form = coupon;
          this.old_json = Object.assign({}, coupon);
          this.$nextTick(() => {
            this.isComputingValidity = false;
          });
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
          this.isUnAuthorised();
        });
      this.isLoading = false;
    },
    buildPayload() {
      const payload = Object.assign({}, this.form);
      [
        "max_discount_amount",
        "usage_limit",
        "validity_days",
        "valid_from",
        "valid_till",
      ].forEach((key) => {
        if (payload[key] === "" || payload[key] === undefined) {
          payload[key] = null;
        }
      });
      if (!payload.plan_ids || payload.plan_ids.length === 0) {
        payload.plan_ids = null;
      }
      return payload;
    },
    async save() {
      try {
        this.isLoading = true;
        await axios
          .patch(`/coupons/${this.$route.params.couponId}`, this.buildPayload())
          .then(async () => {
            await this.saveUserTimestamp(this.old_json, this.form);
            this.isLoading = false;
            this.$router.push("/coupons");
          })
          .catch((error) => {
            this.isLoading = false;
            const data = error.response ? error.response.data : {};
            if (error.response && error.response.status === 401) {
              this.isUnAuthorised();
            } else {
              this.isShowPopUP(
                "Coupon not updated",
                typeof data.errors === "string"
                  ? data.errors
                  : data["sub-title"] || "Please check the form.",
                "error"
              );
            }
          });
      } catch (e) {
        console.log(e);
        this.isLoading = false;
      }
    },
  },
};
</script>
